import { CTU_FIELD_REGISTRY } from '@/config/fieldConfig/ctu';
import type {
  FormDangerousGoodsItem,
  CargoTransportUnitDto,
  FormTemperature,
  PackagingDetail,
} from '@/api/types';

function resolvePath(root: unknown, segments: readonly string[]): unknown {
  let current: unknown = root;
  for (const seg of segments) {
    if (current == null) return undefined;
    current = (current as Record<string, unknown>)[seg];
  }
  return current;
}

type HashSpec<T> = {
  readonly [K in keyof Required<T>]: NonNullable<T[K]> extends
    readonly unknown[] | string | number | boolean
    ? boolean
    : HashSpec<NonNullable<T[K]>> | false;
};

const PACKAGING: HashSpec<PackagingDetail> = {
  code: true,
  description: true,
  count: true,
  type: true,
};
const TEMPERATURE: HashSpec<FormTemperature> = { value: true, unit: true };

// An unhashed field's edit never reaches the error check, so skip only fields a user cannot edit.
export const CARGO_HASH_SPEC: HashSpec<FormDangerousGoodsItem> = {
  unNumber: true,
  properShippingName: true,
  hazardClass: true,
  packingGroup: true,
  technicalName: true,
  subsidiaryHazards: true,
  technicalOrChemicalName: true,
  fireworkClassificationCode: true,
  dsitCode: true,
  t75EndOfHoldingTime: true,
  controlTemperature: TEMPERATURE,
  emergencyTemperature: TEMPERATURE,
  radioactivityDetails: {
    radionuclide: true,
    descriptionOfRadioactiveMaterial: true,
    radioactivity: true,
    radioactiveCategory: true,
    transportIndex: true,
    criticalitySafetyIndex: true,
  },
  fumigationDetails: { fumigantName: true, fumigationDateTime: true, fumigationDosageUsed: true },
  carbonOfOrganicOriginDeclarationDetails: {
    dateOfPacking: true,
    dateOfProduction: true,
    temperatureOnDateOfPacking: true,
  },
  substanceId: true,
  quantity: true,
  netWeight: true,
  grossWeight: true,
  netExplosiveContent: true,
  flashpoint: { temperature: TEMPERATURE, modifier: true },
  declarationParameters: {
    isCoolant: true,
    isConditioner: true,
    isHot: true,
    isMolten: true,
    isStabilized: true,
    isMarinePollutant: true,
    isWaste: true,
    isEmptyUncleaned: true,
    isLimitedQuantity: true,
    isExceptedQuantity: true,
    isSalvagePackaging: true,
    isSalvagePressureReceptacle: true,
    isMixture: true,
    isSolution: true,
    isSample: true,
    isFoodstuffs: true,
    isStabilizedNotByTemperatureControl: true,
    isReportableQuantity: true,
    isPoisonInhalationHazard: true,
    isWettedOrDesensitized: true,
  },
  emsNumber: true,
  outerPackaging: PACKAGING,
  innerPackaging: PACKAGING,
  emergencyContactName: true,
  emergencyContactPhone: true,
  emergencyContact247: true,
  additionalSegregationGroups: true,
  pageNo: false,
  sectionIdx: false,
  cargoId: false,
  ctuId: true,
  dcm: {
    shipperConsignee: true,
    saidToContain: true,
    idNumberPrefix: true,
    isReportableQuantity: true,
    cargoDestination: true,
    loadPort: true,
    specialNotes: true,
    sourceLineStart: false,
    sourceLineEnd: false,
  },
  capacity: true,
  actualDgWeight: true,
  rawSourceFields: false,
};

function flattenHashSpec(spec: object, prefix = ''): string[] {
  return Object.entries(spec).flatMap(([key, value]) => {
    if (value === false) return [];
    const path = prefix + key;
    return value === true ? [path] : flattenHashSpec(value as object, `${path}.`);
  });
}

export const CARGO_HASH_PATHS: readonly string[] = flattenHashSpec(CARGO_HASH_SPEC);
const CARGO_HASH_SEGMENTS = CARGO_HASH_PATHS.map((p) => p.split('.'));

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

export function fnv64(str: string): string {
  let h1 = FNV_OFFSET;
  let h2 = FNV_OFFSET ^ 0x1234567;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, FNV_PRIME);
    h2 = Math.imul(h2 ^ (c + 0x9e3779b9), FNV_PRIME);
  }
  const toHex = (h: number) => (h >>> 0).toString(16).padStart(8, '0');
  return toHex(h1) + toHex(h2);
}

export function hashCargoFields(item: FormDangerousGoodsItem): string {
  return fnv64(CARGO_HASH_SEGMENTS.map((s) => resolvePath(item, s)).join('|'));
}

const CTU_HASH_SEGMENTS = Object.values(
  CTU_FIELD_REGISTRY as Readonly<Record<string, { path: string; hash: boolean }>>
)
  .filter((d) => d.hash)
  .map((d) => d.path.split('.'));

export function hashCtuFields(ctu: CargoTransportUnitDto): string {
  return fnv64(CTU_HASH_SEGMENTS.map((s) => resolvePath(ctu, s)).join('|'));
}

function buildHashMap<T>(
  items: T[],
  getId: (item: T) => string | undefined,
  hashFn: (item: T) => string
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    const id = getId(item);
    if (id) map.set(id, hashFn(item));
  }
  return map;
}

export function buildCargoHashMapWithIndex(items: FormDangerousGoodsItem[]): {
  hashes: Map<string, string>;
  index: Map<string, number>;
} {
  const hashes = new Map<string, string>();
  const index = new Map<string, number>();
  for (let i = 0; i < items.length; i++) {
    const id = items[i].cargoId;
    if (id) {
      hashes.set(id, hashCargoFields(items[i]));
      index.set(id, i);
    }
  }
  return { hashes, index };
}

export function buildCtuHashMap(ctus: CargoTransportUnitDto[]): Map<string, string> {
  return buildHashMap(ctus, (c) => c.ctuId, hashCtuFields);
}

// Adds each changed cargo's parent CTU: the backend labels CTUs only from this list, else the report shows a GUID.
export function resolveChangedCtus(
  changedCtuIds: Set<string>,
  changedCargo: FormDangerousGoodsItem[],
  allCtus: CargoTransportUnitDto[]
): CargoTransportUnitDto[] {
  const wanted = new Set(changedCtuIds);
  for (const cargo of changedCargo) {
    if (cargo.ctuId) wanted.add(cargo.ctuId);
  }
  return allCtus.filter((ctu) => ctu.ctuId != null && wanted.has(ctu.ctuId));
}

// Cargo validation reads CTU state but CTU edits don't change cargo hashes, so resend that cargo or its errors go stale.
export function cargoIdsUnderCtus(
  changedCtuIds: Set<string>,
  items: FormDangerousGoodsItem[]
): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    if (item.ctuId && item.cargoId && changedCtuIds.has(item.ctuId)) ids.add(item.cargoId);
  }
  return ids;
}

export function resolveDeltaChanges(
  changedCargoIds: Set<string>,
  changedCtuIds: Set<string>,
  dangerousGoods: FormDangerousGoodsItem[],
  cargoTransportUnits: CargoTransportUnitDto[],
  cargoIndex: Map<string, number>
): { changedCargo: FormDangerousGoodsItem[]; changedCtus: CargoTransportUnitDto[] } {
  for (const id of cargoIdsUnderCtus(changedCtuIds, dangerousGoods)) changedCargoIds.add(id);

  const changedCargo = [...changedCargoIds]
    .map((id) => {
      const i = cargoIndex.get(id);
      return i !== undefined ? dangerousGoods[i] : undefined;
    })
    .filter(Boolean) as FormDangerousGoodsItem[];

  const changedCtus = resolveChangedCtus(changedCtuIds, changedCargo, cargoTransportUnits);
  return { changedCargo, changedCtus };
}

export function diffHashes(
  current: Map<string, string>,
  lastChecked: Map<string, string>
): Set<string> {
  const changed = new Set<string>();
  for (const [id, hash] of current) {
    if (lastChecked.get(id) !== hash) changed.add(id);
  }
  for (const id of lastChecked.keys()) {
    if (!current.has(id)) changed.add(id);
  }
  return changed;
}
